import Methods from '../Methods';
import INumeric from '../Models/INumeric';
import ResponseMode, { ReturnTypeByMode } from '../ResponseMode';
import Transport, { ITransport } from '../Transports';
import IQueryNumericRequest from '../Requests/IQueryNumericRequest';
import IPatchNumericRequest from '../Requests/IPatchNumericRequest';
import { ensureResponseMode } from '@/utils/utils';
import IMutation from '@/core/Mutations/IMutation';
import ICopyNumericRequest from '@/core/Requests/ICopyNumericRequest';

const baseUri = '/api/v1/numerics';

class NumericService {
    private _transport: ITransport;

    constructor(transport: ITransport) {
        this._transport = transport;
    }

    // Checks whether the task matches the specified filters
    public match(numeric: INumeric, userId: string, filters: IQueryNumericRequest): boolean {
        if (filters.whereId?.length && numeric.id && !filters.whereId.includes(numeric.id)) {
            return false;
        }

        if (filters.whereYear?.length && numeric.year && !filters.whereYear.includes(numeric.year)) {
            return false;
        }

        if (filters.whereTaskId?.length && numeric.taskId && !filters.whereTaskId.includes(numeric.taskId)) {
            return false;
        }

        if (filters.whereAuthorId?.length && numeric.authorId && !filters.whereAuthorId.includes(numeric.authorId)) {
            return false;
        }

        if (
            filters.whereAssigneeId?.length &&
            numeric.assigneeId &&
            !filters.whereAssigneeId.includes(numeric.assigneeId)
        ) {
            return false;
        }

        if (filters.search && numeric.name && numeric.name.indexOf(filters.search) === -1) {
            return false;
        }

        if (numeric.archivedAt && !filters.withArchived) {
            return false;
        }

        return true;
    }

    // Checks if there are any changes in the patch that may affect the result of the filtering.
    public matchPatch(patch: Partial<INumeric>, userId: string, filters: IQueryNumericRequest): boolean {
        if (
            filters.whereId?.length &&
            Object.prototype.hasOwnProperty.call(patch, 'id') &&
            filters.whereId.includes(patch.id as number)
        ) {
            return true;
        }

        if (
            filters.whereYear?.length &&
            Object.prototype.hasOwnProperty.call(patch, 'year') &&
            filters.whereYear.includes(patch.year as number)
        ) {
            return true;
        }

        if (
            filters.whereTaskId?.length &&
            Object.prototype.hasOwnProperty.call(patch, 'taskId') &&
            filters.whereTaskId.includes(patch.taskId as number)
        ) {
            return true;
        }

        if (
            filters.whereAuthorId?.length &&
            Object.prototype.hasOwnProperty.call(patch, 'authorId') &&
            filters.whereAuthorId.includes(patch.authorId as string)
        ) {
            return true;
        }

        if (
            filters.whereAssigneeId?.length &&
            Object.prototype.hasOwnProperty.call(patch, 'assigneeId') &&
            filters.whereAssigneeId.includes(patch.assigneeId as string)
        ) {
            return true;
        }

        if (
            filters.search &&
            Object.prototype.hasOwnProperty.call(patch, 'name') &&
            (patch.name as string).indexOf(filters.search) !== -1
        ) {
            return true;
        }

        if (!filters.withArchived && Object.prototype.hasOwnProperty.call(patch, 'archivedAt') && !patch.archivedAt) {
            return true;
        }

        return false;
    }

    public queryAsync(request?: IQueryNumericRequest): Promise<INumeric[]> {
        return this._transport.invokeAsync<IQueryNumericRequest, INumeric[]>(Methods.Get, baseUri, request);
    }

    public createAsync<T extends ResponseMode = 'status'>(
        responseMode: T = 'status' as T,
    ): Promise<ReturnTypeByMode<INumeric, T>> {
        return this._transport.invokeAsync<void, ReturnTypeByMode<INumeric, T>>(
            Methods.Post,
            `${baseUri}?_rm=${ensureResponseMode(responseMode)}`,
        );
    }

    public patchAsync<T extends ResponseMode = 'status'>(
        id: number,
        request: IPatchNumericRequest,
        responseMode: T = 'status' as T,
    ): Promise<ReturnTypeByMode<INumeric, T>> {
        return this._transport.invokeAsync<IPatchNumericRequest, ReturnTypeByMode<INumeric, T>>(
            Methods.Patch,
            `${baseUri}/${id}?_rm=${ensureResponseMode(responseMode)}`,
            request,
        );
    }

    public archiveAsync(numericId: number): Promise<IMutation[]> {
        return this._transport.invokeAsync<unknown, IMutation[]>(Methods.Patch, `${baseUri}/${numericId}/archive`);
    }

    public migrateAsync(request: ICopyNumericRequest): Promise<IMutation[]> {
        return this._transport.invokeAsync<ICopyNumericRequest, IMutation[]>(
            Methods.Post,
            `${baseUri}/migrate`,
            request,
        );
    }
}

const service = Object.freeze(new NumericService(Transport));
export default service;
